import React, {Component} from 'react';
import {connect} from 'react-redux';
import {
    bindActionCreators,
    Dispatch
} from 'redux';
import _ from 'lodash';
import memoize from 'memoize-one';
import {v4 as uuidV4} from 'uuid';

import {Button} from 'primereact/button';
import {ContextMenu} from 'primereact/contextmenu';
import {Dialog} from 'primereact/dialog';
import {Toast} from 'primereact/toast';
import TreeNode from 'primereact/treenode/TreeNode';

import StackError from '~/shared/src/base/StackError';
import LocalizableError from '~/shared/src/base/LocalizableError';
import Joi from '~/shared/src/utilities/joiExtended';
import {__} from '~/shared/src/utilities/messageCatalog';
import User from '~/shared/src/model/User';
import metaModel from '~/shared/src/utilities/metaModel';
import Item from '~/shared/src/ci/Item';
import {ReferenceAttributesForCreation} from '~/shared/src/ci/Reference';
import dataUtils from '~/shared/src/utilities/dataUtils';
import objectUtils from '~/shared/src/utilities/objectUtils';
import {
    rawModeSchema,
    showAdditionalRawNamesSchema
} from '~/shared/src/validationSchemes/modules/overview';
import {requestBaseParametersSchema} from '~/shared/src/model/BaseRequest';
import {
    CheckItemKeyUniquenessParameters,
    CheckItemKeyUniquenessResult,
    SaveDataResult
} from '~/shared/src/utilities/modules/data';
import {GetBusinessProcessItemIdByDisplayAttributeResult} from '~/shared/src/utilities/modules/businessProcess';

import LoadingMaskPanel from '~/frontend/src/components/base/LoadingMaskPanel';
import Headerbar from '~/frontend/src/components/base/BusinessProcessDesigner/Headerbar';
import DesignerTree, {
    ReferenceType as DesignerTreeReferenceType
} from '~/frontend/src/components/base/BusinessProcessDesigner/DesignerTree';
import Toolbar from '~/frontend/src/components/base/BusinessProcessDesigner/Toolbar';
import ResizeableLeftMenuPanel from '~/frontend/src/components/base/shared/panel/ResizeableLeftMenuPanel';
import ResizeableRightMenuPanel from '~/frontend/src/components/base/shared/panel/ResizeableRightMenuPanel';
import Form from '~/frontend/src/components/base/Form';
import BusinessProcessDesigner, {
    ReferenceType as BusinessProcessDesignerReferenceType
} from '~/frontend/src/components/base/BusinessProcessDesigner';
import AttributesForm from '~/frontend/src/components/base/ItemFinder/detail/PropertiesPanel';

import {
    isDataModified,
    isDataModifiedRecursive,
    MergeMode
} from '~/frontend/src/utilities/dataUtils';
import {
    getPanelIcon as getPanelIconOrig,
    getPanelTitle as getPanelTitleOrig,
    ItemTitleConfig,
    setPanelInfo as setPanelInfoOrig
} from '~/frontend/src/utilities/panelInfo';
import {
    getPanelElusiveData,
    getPanelProps
} from '~/frontend/src/components/base/page/PageTabPanel/utilities';
import {FormPanelProps} from '~/frontend/src/components/base/page/PageTabPanel/definitions';
import {ClearConfigParameters} from '~/shared/src/utilities/modules/form';

import {
    AppDispatch,
    StoreState,
    Thunk
} from '~/frontend/src/store';
import xhr from '~/frontend/src/utilities/xhr';
import {
    ElusiveDataState,
    PanelElusiveDataState
} from '~/frontend/src/reducers/elusiveData';
import {PanelIntercomState} from '~/frontend/src/reducers/intercom';
import {XhrComponentState} from '~/frontend/src/reducers/xhr';
import loadData from '~/frontend/src/actions/data/loadData';
import * as PanelActions from '~/frontend/src/actions/panel/common';
import * as TreeActions from '~/frontend/src/actions/panel/treeView';
import * as IntercomActions from '~/frontend/src/actions/intercom';
import {
    MsgParams,
    setMessage,
    unsetMessage
} from '~/frontend/src/actions/intercom';
import {
    clearElusiveData,
    clearElusiveDataForSubpanels,
    setElusiveData
} from '~/frontend/src/actions/elusiveData';
import * as CiActions from '~/frontend/src/actions/data';
import setParam, {SetParamAction} from '~/frontend/src/actions/panel/common/setParam';
import {UnsetParamAction} from '~/frontend/src/actions/panel/common/unsetParam';
import setPanelDirty, {SetPanelDirty} from '~/frontend/src/actions/panelManager/setPanelDirty';
import setPanelInfo, {SetPanelInfo} from '~/frontend/src/actions/panelManager/setPanelInfo';
import closePanel, {ClosePanel} from '~/frontend/src/actions/panelManager/closePanel';
import hideConfirmCloseDialog from '~/frontend/src/actions/panelManager/hideConfirmCloseDialog';
import saveData, {SaveData} from '~/frontend/src/actions/panel/viewDesigner/saveData';
import {SetParamsAction} from '~/frontend/src/actions/panel/common/setParams';
import addItem, {AddItem} from '~/frontend/src/actions/data/addItem';
import addReference, {AddReference} from '~/frontend/src/actions/data/addReference';
import {DeleteReference} from '~/frontend/src/actions/data/deleteReference';
import cancelRequests from '~/frontend/src/actions/ajax/cancelRequests';
import StringField from '~/frontend/src/components/base/shared/field/valueTypeFields/StringField';
import FieldWrapper from '~/frontend/src/components/base/shared/field/FieldWrapper';
import SchemaDropdown from '~/frontend/src/components/base/shared/field/SchemaDropdown';
import ItemTypeDropdown from '~/frontend/src/components/base/shared/field/ItemTypeDropdown';
import {enumerate} from 'ts-transformer-enumerate';


type PanelElusiveData = PanelElusiveDataState & PanelErrorData & PanelDirtyData & {
    showCouldNotSavePopup? : boolean
};

type Variables = {
    [ variable : string ] : any;
};

const defaultProps = {
    /**
     * The database schema to filter data and metadata for.
     */
    dataContext: 'businessProcessDesigner' as string,
    //
    title: 'BusinessProcessDesignerView' as string,
    //
    schema: 'DATA' as Schema,

    /**
     * The width of the menu on the left side in percent.
     */
    menuWidth: 15 as number,

    /**
     * The width of the menu on the right side in percent.
     */
    menu2Width: 20 as number,

    /**
     * The status to filter data for.
     */
    status: 'active' as Status,

    /**
     * The timestamp to filter data at.
     */
    timestamp: 'now' as string,

    /**
     * The timezone to filter data at.
     */
    timezone: 'Europe/Berlin' as string,

    /**
     * Show the MetaModel inbusinessProcessation as defined (raw) <strong>or</strong> should they be processed by MessageCatalog.
     */
    rawMode: false as boolean,

    /**
     * Show the MetaModel inbusinessProcessation as defined additionally for processed inbusinessProcessation.
     * This applies in the businessProcess: &lt;processed MetaModel inbusinessProcessation&gt; (&lt;defined MetaModel inbusinessProcessation&gt;)
     *
     * Only applicable if <prop>rawMode</prop> is set to false.
     */
    showAdditionalRawNames: false as boolean
};

type DefaultProps = Readonly<typeof defaultProps>;

/**
 * Definition of components props
 * @memberof TreeTest
 * @property {String} dataContext='cypher' The ci-data context of this component
 * @property {Object} items CI-Data as stored in redux store
 * @property {String} itemsVersion Version of CI-Data as stored in redux store
 */
type Props = {
    uid : string;

    /**
     * The ItemId to filter data and metadata for.
     */
    itemId? : number;

    // the ciId of the existing businessProcess
    rootItemId : number

    // the key of the new or existing businessProcess
    rootItemKey : string;

    // the name of the new or existing businessProcess
    rootItemName : string;

    payloadItemType? : string;

    /**
     * Should the MetaModel inbusinessProcessation shown as defined (raw) <strong>or</strong> should they be processed by MessageCatalog.
     */
    rawMode? : boolean;

    /**
     * Should the MetaModel inbusinessProcessation as defined additionally shown for processed inbusinessProcessation <strong>or</strong> not.
     * This applies in the businessProcess: &lt;processed MetaModel inbusinessProcessation&gt; (&lt;defined MetaModel inbusinessProcessation&gt;)
     *
     * Only applicable if <prop>rawMode</prop> is set to false.
     */
    showAdditionalRawNames? : boolean;

    selectedStepId? : number;

    selectedConnectionId? : number;

    selectedDataObjectId? : number;

    referenceToCreateForNew? : ReferenceAttributesForCreation;

    /**
     * The props for sub panels
     */
    subPanelProps? : any;

    onSave? : ( rootItemId : number, dataContext : string ) => void;

    /**
     * The full elusiveData
     */
    allElusiveData? : ElusiveDataState;

    /**
     * The elusive data (not persisted by redux store) for this component.
     * A storage for temporary needed data (not persisted to local storage via redux store persist), like
     *  - lastParams: The stringified parameters of the last loaded search. Used to determine if a new result have to be loaded from the backend.
     *  - gridData: The ordered itemIds to display in the grid.
     *  - totalRecords: The total result data count to be displayed in the DataTable.
     *
     * Will be mapped from global state.
     */
    elusiveData? : PanelElusiveData;

    /**
     * The intercom messages for this component.
     *
     * Will be mapped from global state.
     */
    intercomMessages? : PanelIntercomState;

    /**
     * The current user.
     *
     * Will be mapped from global state.
     */
    user? : User;

    /**
     * The running requests belonging to this component (uid).
     *
     * Will be mapped from global state.
     */
    xhrRequestsForComponent? : XhrComponentState;
};

const Actions = Object.assign( { loadData, setMessage, unsetMessage, setElusiveData, clearElusiveData, clearElusiveDataForSubpanels, setPanelInfo, setPanelDirty, addItem, addReference, closePanel }, PanelActions, TreeActions, IntercomActions, CiActions );

type SetParam = <K extends keyof ComponentProps>( panelKey : string, paramKey : K, paramValue : ComponentProps[K] ) => SetParamAction;
type UnsetParam = ( panelKey : string, paramKey : keyof ComponentProps ) => UnsetParamAction;
type SetParams = ( panelKey : string, panelProps : Partial<ComponentProps> ) => SetParamsAction;

/**
 * Function props mapped by mapDispatchToProps
 */
type ActionPropsRaw = {
    //updatePanelProps: typeof updatePanelProps;
    loadData : typeof loadData;
    setParam : SetParam;
    setParams : SetParams;
    unsetParam : UnsetParam;
    setMessage : typeof setMessage;
    unsetMessage : typeof unsetMessage;
    setPanelInfo : SetPanelInfo;
    setElusiveData : typeof setElusiveData;
    clearElusiveData : typeof clearElusiveData;
    clearElusiveDataForSubpanels : typeof clearElusiveDataForSubpanels;
    setPanelDirty : SetPanelDirty;
    deleteReference : DeleteReference;
    addItem : AddItem;
    addReference : AddReference;

};

type ReplaceReturnType<T extends ( ...a : any ) => any, TNewReturn> = ( ...a : Parameters<T> ) => TNewReturn;
type ActionProps = {
    [K in keyof ActionPropsRaw] : ReplaceReturnType<ActionPropsRaw[K], ReturnType<ActionPropsRaw[K]> extends Thunk<any> ? ReturnType<ReturnType<ActionPropsRaw[K]>> : void>;
} & {
    closePanel : ClosePanel;
};

type AllProps = Props & DefaultProps & ActionProps;
export type ComponentProps = Partial<DefaultProps> & Props;

type State = {
    // the ciId of the currently selected tree node
    rootItemId : number;
    selectedItemParentId? : number;

    isDirty : boolean;
    errorCount : number;
    errorTooltip? : string | React.ReactNode | ( string | React.ReactNode )[];
    errorMessage? : string | React.ReactNode | ( string | React.ReactNode )[];

    /**
     * Counts up on menu scroll. Used to fire update for arrows
     */
    scrollIndicator : number;

    initialized : boolean;

    businessProcessItemTypeId : number;

    showCreateNewBusinessProcessDialog : boolean;

    createNewBusinessProcessDialogKey? : string;
    createNewBusinessProcessDialogKeyError? : string;
    createNewBusinessProcessDialogSchema : Schema;
    createNewBusinessProcessDialogItemType? : string;

    mobile : boolean;
};


const schemata  = enumerate<YnSchema>(),
      getMobile = () => matchMedia( 'screen and (max-width: 960px)' ).matches ? true : false;


/**
 * Playground to test Tree implementation
 */
class BusinessProcessDesignerView extends Component<AllProps, State> {

    static defaultProps = defaultProps;

    // panel manager config
    static panelType = 'BusinessProcessDesignerView';
    static panelTabTitle = 'YN.base.frontend.businessProcessDesigner.tabTitle';
    static panelTabIcon = 'itemTypeIcon:CFG:BusinessProcess';

    static panelPropsSchemaMsgIdPrefix = 'YN.base.frontend.businessProcessDesigner';
    static panelPropsSchema = Joi.object()
        // panelSchema defaults
        .concat( requestBaseParametersSchema )
        .concat( Joi.object( {
            panelType      : Joi.string().valid( BusinessProcessDesignerView.panelType ),
            rootItemType   : Joi.string(),
            panelTabTitle  : Joi.string().default( BusinessProcessDesignerView.panelTabTitle ),
            panelTabIcon   : Joi.string().default( BusinessProcessDesignerView.panelTabIcon ),
            rootItemId     : Joi.number().description( 'Id of the businessProcess.' ),
            rootItemKey    : Joi.string().description( 'Key of businessProcess' ),
            payloadItemType: Joi.string().description( 'Item Type of the payload root item (only needed for new businessProcesss)' ),
            menuWidth      : Joi.number()
                .default( 15 )
                .description( 'The width of the left menu panel in percent of the available width.' ),
            menu2Width     : Joi.number()
                .default( 30 )
                .description( 'The width of the right menu panel in percent of the available width.' ),
            // this is only for compatibility with ItemFinder overview (if we open this panel from there)
            rawMode                : rawModeSchema,
            showAdditionalRawNames : showAdditionalRawNamesSchema,
            title                  : Joi.string(),
            referenceToCreateForNew: Joi.object( {
                srcItemId     : Joi.number()
                    .required(),
                destItemId    : Joi.number()
                    .required(),
                referenceSetId: Joi.number()
                    .required(),
                statusId      : Joi.number()
                    .required()
            } ),
            onSave                 : Joi.function()
        } ) );

    /**
     * YOONITE lifecycle method
     */
    static onClosePanelAction( props : ComponentProps ) {
        return async ( dispatch : AppDispatch, getState : () => StoreState ) => {

            // abort running request
            dispatch( cancelRequests( props.uid ) );

            // clear global data
            if ( props.dataContext ) {
                delete global.data[ props.dataContext ];
                delete global.data[ `${props.dataContext}-meta` ];
                delete global.data[ `${props.dataContext}_data` ];
                delete global.data[ `${props.dataContext}_DataObjects` ];
            }

            // clear backend data of all loaded forms
            for ( const subPanelProps of Object.values( props.subPanelProps ?? {} ) as any[] ) {
                if ( subPanelProps.moduleTransformer === 'form' && subPanelProps.configHash ) {
                    const params : ClearConfigParameters = {
                        configHash: subPanelProps.configHash
                    };

                    await xhr.post( `${BASE_URL}/api/form/clearConfig`, params, undefined, props.uid, props.uid, dispatch );
                }
            }
        }
    }

    /**
     * YOONITE lifecycle method
     */
    static onSavePanelAction( props : ComponentProps ) {
        return async ( dispatch : AppDispatch, getState : () => StoreState ) => {
            await BusinessProcessDesignerView.savePanelData(
                dispatch,
                getState,
                props
            );
        }
    }

    /**
     * YOONITE lifecycle method
     */
    static onSaveAndClosePanelAction( props : ComponentProps ) {
        return async ( dispatch : AppDispatch, getState : () => StoreState ) => {
            const result = await BusinessProcessDesignerView.savePanelData(
                dispatch,
                getState,
                props,
                [
                    props.uid,
                    'notification',
                    'saveSuccess',
                    'closePanel'
                ]
            );

            // no save error -> close panel
            if ( result !== false && result.success ) {
                await dispatch( closePanel( props.uid ) );
            }
        }
    }

    /**
     * YOONITE lifecycle method
     */
    static buildPanelPathFromProps( props : Pick<AllProps, 'dataContext' | 'rootItemId' | 'rootItemKey'> ) : string | undefined {
        const {
                  dataContext,
                  rootItemId,
                  rootItemKey
              }      = props,
              itemId = rootItemId
                  ?? dataUtils.findItem( dataContext, 'BusinessProcess', rootItemKey )?.id
                  ?? 0,
              item   = global.data[ dataContext ]?.items?.[ itemId ];

        let retVal : string | undefined;

        if ( item ) {
            retVal = `#${BusinessProcessDesignerView.panelType}/${encodeURIComponent( item.displayAttributeValue )}`;
        }

        return retVal;
    }

    /**
     * YOONITE lifecycle method
     */
    static async buildPanelPropsFromPath( path : string ) : Promise<Partial<ComponentProps>> {
        const retVal : Partial<ComponentProps> = {},
              parts                            = path.match( /#([^?]*)(\?(.*))?/ ),
              params                           = parts?.[ 1 ]?.split( '/' ) ?? [],
              stackError                       = new StackError( 'YN.base.resolvePanelPropsFromURL.error.title', { path: path } );

        // params[ 0 ] = panelType

        // params[ 1 ] = rootItem.displayAttributeValue
        if ( params?.[ 1 ] ) {
            const displayAttributeValue = decodeURIComponent( params[ 1 ] );

            // resolve viewKey and rootItemId
            const res    = await xhr.post(
                      `${BASE_URL}/api/businessProcess/getBusinessProcessItemIdByDisplayAttribute`,
                      {
                          displayAttributeValue
                      }
                  ),
                  result = res?.data as GetBusinessProcessItemIdByDisplayAttributeResult;

            if ( result ) {
                if ( result.data.rootItemId ) {
                    retVal.rootItemId = result.data.rootItemId;
                }
                else {
                    stackError.add( new LocalizableError( 'YN.base.designer.resolvePanelPropsFromURL.error.couldNotResolveRootItemId', { attribute: displayAttributeValue } ) );
                }
            }
        }
        else {
            stackError.add( new LocalizableError( 'YN.base.designer.resolvePanelPropsFromURL.error.couldNotResolveRootItem' ) );
        }

        if ( stackError.hasErrors() ) {
            throw ( stackError.toString() );
        }

        return retVal;
    }

    static async doSaveData( uid : string, dispatch : AppDispatch, successMsgParams : MsgParams | undefined = undefined ) : ReturnType<ReturnType<SaveData>> {
        const props                                              = getPanelProps( uid )?.panelProps as ComponentProps,
              elusiveData                                        = getPanelElusiveData( uid ),
              propsWithDefaults : Required<DefaultProps> & Props = {
                  ...defaultProps,
                  ...props
              },
              {
                  rootItemId,
                  selectedStepId,
                  onSave,
                  subPanelProps
              }                                                  = propsWithDefaults,
              rootItemIdForSave                                  = rootItemId ?? elusiveData?.data?.rootItemId,
              result                                             = await dispatch( saveData( uid, 'CFG', rootItemIdForSave, undefined, undefined, undefined, successMsgParams ) );

        // remap selectedLayoutElement id
        if ( selectedStepId && result && result.data.itemMetaDataMapping[ String( selectedStepId ) as unknown as number ] ) {
            dispatch( setParam( uid, 'selectedStepId', result.data.itemMetaDataMapping[ String( selectedStepId ) as unknown as number ].id ) );
        }

        // remap activeTabContainerIndexes
        if ( result && result.data.referenceMetaDataMapping && Object.keys( result.data.referenceMetaDataMapping ).length ) {
            for ( const subPanelKey of Object.keys( subPanelProps ) as string[] ) {
                if ( subPanelProps[ subPanelKey ].activeTabContainerIndexes ) {
                    const activeTabContainerIndexes    = subPanelProps[ subPanelKey ].activeTabContainerIndexes,
                          newActiveTabContainerIndexes = { ...activeTabContainerIndexes };

                    let remapped : boolean = false;

                    for ( const realTabContainerLayoutElementId of Object.keys( newActiveTabContainerIndexes ) ) {
                        if ( newActiveTabContainerIndexes[ realTabContainerLayoutElementId ] < 0 && result.data.referenceMetaDataMapping[ newActiveTabContainerIndexes[ realTabContainerLayoutElementId ] ] ) {
                            remapped = true;
                            newActiveTabContainerIndexes[ realTabContainerLayoutElementId ] = result.data.referenceMetaDataMapping[ newActiveTabContainerIndexes[ realTabContainerLayoutElementId ] ].id;
                        }
                    }

                    if ( remapped ) {
                        dispatch( setParam(
                            `${uid}_${subPanelKey}`,
                            'activeTabContainerIndexes',
                            newActiveTabContainerIndexes
                        ) );
                    }
                }
            }
        }

        if ( onSave ) {
            // remap rootItem id
            if ( result && result.data.itemMetaDataMapping[ String( rootItemIdForSave ) as unknown as number ] ) {
                onSave( result.data.itemMetaDataMapping[ String( rootItemIdForSave ) as unknown as number ].id, uid );
            }
            else {
                onSave( rootItemIdForSave, uid );
            }
        }

        return result;
    }

    static async savePanelData( dispatch : AppDispatch, getState : () => StoreState, props : ComponentProps, successMsgParams : MsgParams | undefined = undefined ) : Promise<SaveDataResult | false> {
        const propsWithDefaults : DefaultProps & Props = {
                  ...defaultProps,
                  ...props
              },
              {
                  uid
              }                                        = propsWithDefaults,
              allElusiveData                           = getState()?.elusiveData;

        let retVal : Promise<SaveDataResult | false> = Promise.resolve( false ),
            hasError : boolean                       = false;

        if ( allElusiveData ) {
            for ( const elusiveDataPanelKey of Object.keys( allElusiveData ) as string[] ) {
                if ( elusiveDataPanelKey.indexOf( `${uid}_` ) === 0 ) {
                    const panelElusiveData = allElusiveData[ elusiveDataPanelKey ] as PanelElusiveDataState & PanelErrorData & PanelDirtyData;

                    if ( panelElusiveData.errorData?.count ) {
                        hasError = true;
                        break;
                    }
                }
            }
        }

        // panel has errors -> show popup
        if ( hasError ) {
            dispatch( hideConfirmCloseDialog( uid ) );
            dispatch( setElusiveData( uid, { showCouldNotSavePopup: true } ) );
        }

        // panel has no errors -> save
        else {
            retVal = BusinessProcessDesignerView.doSaveData( uid, dispatch, successMsgParams );
        }

        return retVal;
    }

    menuContentPanelContentStyle = { padding: 0 };

    isLoading : boolean = false;

    cm : InstanceType<typeof ContextMenu> | null = null;
    toast : Toast | null = null;
    tree : DesignerTreeReferenceType | null = null;
    businessProcessDesigner : BusinessProcessDesignerReferenceType | null = null;

    getTreeContextMenu = ( treeData : TreeNode.TreeNode[], key : string ) => [
        {
            label  : 'Collapse all',
            icon   : 'far fa-minus-square',
            command: function() {
                this.setState( { [ key ]: {} } );
            }.bind( this )
        },
        {
            label  : 'Expand all',
            icon   : 'far fa-plus-square',
            command: function() {
                const expandedKeys : {
                    [ key : string ] : true
                } = {};
                const expandNode = ( node : TreeNode.TreeNode ) => {
                    if ( node.children?.length ) {
                        expandedKeys[ node.key! ] = true;

                        for ( const child of node.children ) {
                            expandNode( child );
                        }
                    }
                }

                for ( const node of treeData ) {
                    expandNode( node );
                }

                this.setState( { [ key ]: expandedKeys } );
            }.bind( this )
        }
    ];

    getPanelIcon = getPanelIconOrig;
    getPanelTitle = getPanelTitleOrig;
    setPanelInfo = setPanelInfoOrig;

    /**
     * React Component constructor
     * @param {Object} props
     */
    constructor( props : AllProps ) {
        super( props );

        this.state = {
            rootItemId: 0 as number,

            isDirty   : false,
            errorCount: 0,

            scrollIndicator: 0 as number,

            initialized: false,

            businessProcessItemTypeId: metaModel[ 'CFG' ].getItemType( 'BusinessProcess' )?.id ?? 0,

            showCreateNewBusinessProcessDialog: false,

            createNewBusinessProcessDialogKey     : props.rootItemKey ? props.rootItemKey : undefined,
            createNewBusinessProcessDialogSchema  : 'DATA',
            createNewBusinessProcessDialogItemType: props.payloadItemType ? props.payloadItemType : undefined,

            mobile: getMobile()
        };

        this.checkForIntercomMessages = memoize( this.checkForIntercomMessages );
        this.getDesignerTree = memoize( this.getDesignerTree, this.getDesignerTreeEqual );
        this.getForm = memoize( this.getForm, this.getFormEqual );
        this.getBusinessProcessDesigner = memoize( this.getBusinessProcessDesigner, this.getBusinessProcessDesignerEqual );
        this.getHeaderbar = memoize( this.getHeaderbar );
        this.getPanelIcon = memoize( this.getPanelIcon );
        this.getPanelTitle = memoize( this.getPanelTitle );
        this.getToolbar = memoize( this.getToolbar );
        this.setPanelInfo = memoize( this.setPanelInfo );

        this.onMenuScroll = _.throttle( this.onMenuScroll, 100, { trailing: true } );

        this.onMatchMobileChangeHandler = this.onMatchMobileChangeHandler.bind( this );
        this.onMenu2WidthChange = this.onMenu2WidthChange.bind( this );
        this.onMenuWidthChange = this.onMenuWidthChange.bind( this );

        this.checkForCreateNewBusinessProcessPrerequisits = this.checkForCreateNewBusinessProcessPrerequisits.bind( this );
        this.onCreateNewBusinessProcessDialogOkButtonClick = this.onCreateNewBusinessProcessDialogOkButtonClick.bind( this );
        this.onCreateNewBusinessProcessDialogHide = this.onCreateNewBusinessProcessDialogHide.bind( this );
        this.onCreateNewBusinessProcessDialogSetKey = this.onCreateNewBusinessProcessDialogSetKey.bind( this );
        this.onCreateNewBusinessProcessDialogSetSchema = this.onCreateNewBusinessProcessDialogSetSchema.bind( this );
        this.onCreateNewBusinessProcessDialogSetItemType = this.onCreateNewBusinessProcessDialogSetItemType.bind( this );

        this.onAddNewBusinessProcessStep = this.onAddNewBusinessProcessStep.bind( this );
        this.onReloadButtonClick = this.onReloadButtonClick.bind( this );
        this.onSelectStepOrConnection = this.onSelectStepOrConnection.bind( this );
        this.onSelectDataObject = this.onSelectDataObject.bind( this );
    }

    /**
     * REDUX stuff
     */
    static getDerivedStateFromProps( props : AllProps, state : State ) : Partial<State> {
        const {
                  uid,
                  rootItemId,
                  rootItemKey,
                  dataContext,
                  subPanelProps,
                  allElusiveData
              }                                              = props,
              tooltipErrors : ( string | React.ReactNode )[] = [],
              messageErrors : ( string | React.ReactNode )[] = [];

        let isDirty : boolean = rootItemId ? isDataModifiedRecursive( dataContext, 'CFG', rootItemId ) : isDataModified( dataContext, 'CFG' ),
            errorCount        = 0;

        if ( allElusiveData ) {
            for ( const elusiveDataPanelKey of Object.keys( allElusiveData ) as string[] ) {
                if ( elusiveDataPanelKey.indexOf( `${uid}_` ) === 0 ) {
                    const panelElusiveData = allElusiveData[ elusiveDataPanelKey ] as PanelElusiveDataState & PanelErrorData & PanelDirtyData,
                          panelKey         = elusiveDataPanelKey.replace( `${uid}_`, '' );

                    // only check direct sub businessProcesss
                    if ( panelKey.match( /^businessProcess-/ ) && panelKey.indexOf( '_' ) === -1 ) {
                        const panelProps              = subPanelProps?.[ panelKey ],
                              businessProcessRootItem = panelProps
                                  ? global.data[ dataContext ]?.items[ panelProps.rootItemId ]
                                  : undefined,
                              elementName             = businessProcessRootItem
                                  ? businessProcessRootItem.name
                                      ? businessProcessRootItem.name
                                      : businessProcessRootItem?.key
                                  : '',
                              elementType             = businessProcessRootItem ? businessProcessRootItem.itemType.label : 'Element';

                        isDirty = isDirty || !!panelElusiveData.isDirty;

                        if ( panelElusiveData.errorData?.count ) {
                            errorCount += panelElusiveData.errorData.count;

                            if ( panelElusiveData.errorData.tooltip?.length ) {
                                tooltipErrors.push(
                                    <>
                                        <div className="yn-tooltip-header2">
                                            {__( 'YN.base.frontend.businessProcessDesigner.error.tooltip.title', { type: elementType, name: elementName } )}
                                        </div>
                                        {panelElusiveData.errorData.tooltip}
                                    </>
                                );

                                if ( panelElusiveData.errorData.message?.length ) {
                                    messageErrors.push(
                                        <>
                                            <div className="yn-error-subtitle">
                                                {__( 'YN.base.frontend.businessProcessDesigner.error.message.title', { type: elementType, name: elementName } )}
                                            </div>
                                            <hr className="yn-error-ruler"/>
                                            <div className="yn-error-suberror">
                                                {panelElusiveData.errorData.message}
                                            </div>
                                        </>
                                    );
                                }
                            }
                        }
                    }
                }
            }
        }

        return {
            rootItemId  : rootItemId || ( dataContext && rootItemKey ? dataUtils.findItem( dataContext, 'BusinessProcess', rootItemKey )?.id ?? 0 : state.rootItemId ),
            isDirty,
            errorCount,
            errorTooltip: errorCount
                ? <div key="error" className="yn-tooltip-error">
                    <div className="yn-tooltip-header">
                        {__( 'YN.base.frontend.dataTable.tooltip.error.title' )}
                    </div>
                    {tooltipErrors}
                </div>
                : undefined,
            errorMessage: errorCount
                ? <>
                    <div className="yn-error-message">
                        {__( 'YN.base.frontend.dataTable.couldNotSaveWhileErrors.text' )}
                    </div>
                    <hr className="yn-error-ruler"/>
                    <div className="yn-error-suberror">
                        {messageErrors}
                    </div>
                </>
                : undefined
        };
    }

    /**
     * REACT lifecycle method
     */
    componentDidMount() {
        matchMedia( 'screen and (max-width: 960px)' ).addEventListener( 'change', this.onMatchMobileChangeHandler );

        this.afterRender();

        if ( !this.props.elusiveData ) {
            if ( this.props.rootItemId || this.props.rootItemKey ) {
                void this.loadData();
            }
            else {
                this.checkForCreateNewBusinessProcessPrerequisits();
            }
        }
        else {
            this.setState( {
                initialized: true
            } );
        }
    }

    /**
     * REACT lifecycle method
     */
    componentWillUnmount() {
        matchMedia( 'screen and (max-width: 960px)' ).removeEventListener( 'change', this.onMatchMobileChangeHandler );
    }

    /**
     * REACT lifecycle method
     */
    componentDidUpdate( prevProps : Readonly<AllProps>, prevState : Readonly<State> ) {
        this.afterRender();

        // key changed or dialog shown
        if (
            prevState.createNewBusinessProcessDialogKey !== this.state.createNewBusinessProcessDialogKey
            || ( !prevState.showCreateNewBusinessProcessDialog && this.state.showCreateNewBusinessProcessDialog )
        ) {
            this.setState( { createNewBusinessProcessDialogKeyError: __( 'YN.base.finder.item.key.check.unique' ) } );
            this.checkKeyUniqueness();
        }
    }

    /**
     * REACT lifecycle method
     */
    shouldComponentUpdate( nextProps : AllProps, nextState : State ) : boolean {
        if (
            ( nextState.isDirty !== undefined && nextState.isDirty !== this.state.isDirty )
        ) {
            this.props.setPanelDirty( this.props.uid, nextState.isDirty );
        }

        return !this.isLoading;
    }

    async loadData() : Promise<boolean> {
        this.isLoading = true;

        const {
                  dataContext,
                  rootItemId,
                  rootItemKey,
                  uid
              }      = this.props,
              retVal = await this.props.loadData?.(
                  'businessProcess/loadConfigData',
                  dataContext,
                  dataContext,
                  undefined,
                  'CFG',
                  {
                      params   : rootItemId ? {
                          viewId: rootItemId
                      } : {
                          viewKey: rootItemKey
                      },
                      mergeMode: 'keepExisting' as MergeMode
                  },
                  undefined,
                  resonseData => {
                      if ( resonseData?.data.rootItemId && rootItemId !== resonseData.data.rootItemId ) {
                          this.props.setParam( uid, 'rootItemId', resonseData.data.rootItemId );
                      }

                      if ( !resonseData?.data.items.length ) {
                          this.checkForCreateNewBusinessProcessPrerequisits();
                      }
                      else {
                          this.setState( {
                              initialized: true
                          } );
                      }
                  }
              );

        this.isLoading = false;

        return retVal;
    }

    afterRender( props : AllProps = this.props as AllProps ) {
        const {
                  uid,
                  dataContext,
                  rootItemId,
                  rootItemKey,
                  intercomMessages
              }                                             = props,
              {
                  isDirty,
                  errorCount
              }                                             = this.state,
              icon                                          = this.getPanelIcon( BusinessProcessDesignerView.panelTabIcon ),
              itemTitleConfig : ItemTitleConfig | undefined = rootItemId
                  ? {
                      dataContext,
                      id: rootItemId
                  }
                  : undefined,
              title                                         = this.getPanelTitle( BusinessProcessDesignerView.panelTabTitle, undefined, itemTitleConfig ),
              path                                          = BusinessProcessDesignerView.buildPanelPathFromProps( { dataContext, rootItemId, rootItemKey } );

        this.checkForIntercomMessages( intercomMessages );

        this.setPanelInfo( uid, icon, title, undefined, isDirty, !!errorCount, path );
    }

    /**
     * Checks if there are intercom messages present and shows them if.
     *
     * @private
     */
    checkForIntercomMessages( intercomMessages? : PanelIntercomState ) {
        // check for toasts
        if ( this.toast && intercomMessages?.toast ) {
            const toastKeys = Object.keys( intercomMessages.toast );
            for ( const key of toastKeys ) {
                // show toast
                if ( intercomMessages.toast[ key ] ) {
                    this.toast.show( intercomMessages.toast[ key ] );
                }

                // clear toast message
                this.props.unsetMessage?.( this.props.uid, 'toast', key as string );
            }
        }
    }

    checkForCreateNewBusinessProcessPrerequisits() {
        const {
                  createNewBusinessProcessDialogItemType
              } = this.state;

        // no root DataObject ItemType -> show dialog
        if ( !createNewBusinessProcessDialogItemType ) {
            this.setState( {
                initialized                       : true,
                showCreateNewBusinessProcessDialog: true
            } );
        }
        else {
            this.setState( {
                initialized: true
            } );

            this.createNewBusinessProcess();
        }
    }

    /**
     * Checks the given key for uniqueness within the given ItemType against the backend.
     *
     * @private
     */
    checkKeyUniqueness() {
        const {
                  uid
              }          = this.props,
              {
                  businessProcessItemTypeId,
                  createNewBusinessProcessDialogKey
              }          = this.state,
              requestUid = 'checkKeyUniqueness';

        if ( businessProcessItemTypeId && createNewBusinessProcessDialogKey ) {

            // abort running check
            if ( this.props.xhrRequestsForComponent?.[ requestUid ] ) {
                xhr.abort( uid, requestUid );
            }

            const doLoad = async () => {
                const params : CheckItemKeyUniquenessParameters = {
                          schema  : 'CFG',
                          status  : 'all',
                          itemType: businessProcessItemTypeId,
                          key     : createNewBusinessProcessDialogKey
                      },
                      response                                  = await xhr.post(
                          `${BASE_URL}/api/data/checkItemKeyUniqueness`,
                          params,
                          undefined,
                          uid,
                          requestUid
                      );

                if ( response?.data ) {
                    const responseData = response.data as CheckItemKeyUniquenessResult;

                    if ( responseData.success && !responseData.data.keyIsUnique ) {
                        this.setState( { createNewBusinessProcessDialogKeyError: __( 'YN.base.frontend.businessProcessDesigner.createNewDialog.key.error.notUnique' ) } );
                    }
                    else {
                        this.setState( { createNewBusinessProcessDialogKeyError: '' } );
                    }
                }
            };

            doLoad().catch( ( error ) => {
                console.log( error );
            } );
        }
    }

    createNewBusinessProcess() {
        const {
                  uid,
                  dataContext,
                  referenceToCreateForNew,
                  rootItemName
              }              = this.props,
              {
                  businessProcessItemTypeId,
                  createNewBusinessProcessDialogKey,
                  createNewBusinessProcessDialogSchema,
                  createNewBusinessProcessDialogItemType
              }              = this.state,
              activeStatusId = metaModel[ 'CFG' ].getStatus( 'active' )!.id;


        if ( createNewBusinessProcessDialogKey && createNewBusinessProcessDialogItemType ) {
            const businessProcess             = this.props.addItem(
                      dataContext,
                      {
                          itemTypeId: businessProcessItemTypeId,
                          key       : createNewBusinessProcessDialogKey,
                          name      : rootItemName ?? createNewBusinessProcessDialogItemType,
                          statusId  : activeStatusId
                      },
                      'CFG'
                  ),
                  rootDataObject              = this.props.addItem(
                      dataContext,
                      {
                          itemTypeId: metaModel[ 'CFG' ].getItemType( 'DataObject' )!.id,
                          key       : `${createNewBusinessProcessDialogKey}:${createNewBusinessProcessDialogItemType}:${uuidV4().substring( 0, 8 )}`,
                          name      : createNewBusinessProcessDialogItemType,
                          statusId  : activeStatusId,
                          properties: [
                              {
                                  propertySetId: metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( 'DataObject', 'ObjectSchema' )!.id,
                                  value        : createNewBusinessProcessDialogSchema.toLowerCase(),
                                  statusId     : activeStatusId
                              },
                              {
                                  propertySetId: metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( 'DataObject', 'ObjectType' )!.id,
                                  value        : createNewBusinessProcessDialogItemType,
                                  statusId     : activeStatusId
                              }
                          ]
                      },
                      'CFG'
                  ),
                  businessProcessStepItemType = metaModel[ 'CFG' ].getItemType( 'BusinessProcessStep' )!,
                  businessProcessStep         = this.props.addItem(
                      dataContext,
                      {
                          itemTypeId: businessProcessStepItemType.id,
                          key       : `${createNewBusinessProcessDialogKey}:step:${uuidV4().substring( 0, 8 )}`,
                          name      : `1. ${businessProcessStepItemType.label}`,
                          statusId  : activeStatusId,
                          properties: [
                              {
                                  propertySetId: metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( 'BusinessProcessStep', 'Order' )!.id,
                                  value        : 1,
                                  statusId     : activeStatusId
                              },
                              {
                                  propertySetId: metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( 'BusinessProcessStep', 'SubOrder' )!.id,
                                  value        : 1,
                                  statusId     : activeStatusId
                              }
                          ]
                      },
                      'CFG'
                  );

            if ( businessProcess && rootDataObject && businessProcessStep ) {
                this.props.addReference(
                    dataContext,
                    {
                        srcItemId     : rootDataObject.id,
                        destItemId    : businessProcess.id,
                        referenceSetId: metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( 'DataObject', 'BusinessProcess', 'isRootOf', 'src' )![ 0 ].id,
                        statusId      : activeStatusId
                    },
                    'CFG'
                );
                this.props.addReference(
                    dataContext,
                    {
                        srcItemId     : businessProcessStep.id,
                        destItemId    : businessProcess.id,
                        referenceSetId: metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( 'BusinessProcessStep', 'BusinessProcess', 'belongsTo', 'src' )![ 0 ].id,
                        statusId      : activeStatusId
                    },
                    'CFG'
                );

                // add additionalReference (replace rootItemId by businessProcess.id)
                if ( referenceToCreateForNew ) {
                    if ( referenceToCreateForNew.srcItemId < 0 ) {
                        referenceToCreateForNew.srcItemId = businessProcess.id;
                    }
                    else {
                        referenceToCreateForNew.destItemId = businessProcess.id;
                    }

                    this.props.addReference( dataContext, referenceToCreateForNew, 'CFG' );
                    this.props.setParam( uid, 'referenceToCreateForNew', undefined );
                }

                this.props.setParam( uid, 'rootItemId', businessProcess.id );
            }
        }
    }

    onAddNewBusinessProcessStep() {
        this.businessProcessDesigner?.addStep();
    }

    onReloadButtonClick() {
        const {
                  uid,
                  dataContext
              } = this.props;

        if ( dataContext ) {
            this.isLoading = true;

            this.props.clearElusiveData?.( uid );
            this.props.clearElusiveDataForSubpanels?.( uid );

            // clean data
            global.data[ dataContext ] = {
                items     : [],
                references: [],
                schema    : global.data[ dataContext ].schema
            };

            window.setTimeout(
                () => {
                    void this.loadData();
                },
                1
            );
        }
    }

    onMenuScroll() {
        this.setState( { scrollIndicator: this.state.scrollIndicator! + 1 } )
    }

    /**
     * Handler for menu width change.
     *
     * @private
     */
    onMenuWidthChange( width : number ) {
        this.props.setParam?.( this.props.uid, 'menuWidth', width );
    }

    /**
     * Handler for menu width change.
     *
     * @private
     */
    onMenu2WidthChange( width : number ) {
        this.props.setParam?.( this.props.uid, 'menu2Width', width );
    }

    onCreateNewBusinessProcessDialogOkButtonClick() {
        this.createNewBusinessProcess();
        this.setState( { showCreateNewBusinessProcessDialog: false } );
    }

    onCreateNewBusinessProcessDialogHide() {
        this.props.closePanel( this.props.uid );
    }

    onCreateNewBusinessProcessDialogSetKey( value : string | undefined ) {
        let key      = '',
            keyError = '';

        if ( value !== undefined ) {
            key = value;

            if ( !key ) {
                keyError = __( 'YN.base.finder.item.key.error.empty' );
            }
            else if ( / /.test( key ) ) {
                keyError = __( 'YN.base.finder.item.key.error.whitespaces' );
            }

            this.setState( { createNewBusinessProcessDialogKey: key, createNewBusinessProcessDialogKeyError: keyError } );
        }
    }

    onCreateNewBusinessProcessDialogSetSchema( value : string ) {
        const {
                  createNewBusinessProcessDialogItemType,
                  createNewBusinessProcessDialogSchema
              }               = this.state,
              oldGeneratedKey = createNewBusinessProcessDialogSchema === 'CFG'
                  ? `CFG:${createNewBusinessProcessDialogItemType ?? ''}`
                  : createNewBusinessProcessDialogItemType ?? '';

        let {
                createNewBusinessProcessDialogKey,
                createNewBusinessProcessDialogKeyError
            } = this.state;

        if ( !createNewBusinessProcessDialogKey || createNewBusinessProcessDialogKey === oldGeneratedKey ) {
            createNewBusinessProcessDialogKey = value === 'CFG' ? 'CFG:' : '';
            createNewBusinessProcessDialogKeyError = undefined;
        }

        this.setState( {
            createNewBusinessProcessDialogSchema  : value as Schema,
            createNewBusinessProcessDialogItemType: undefined,
            createNewBusinessProcessDialogKey,
            createNewBusinessProcessDialogKeyError
        } );
    }

    onCreateNewBusinessProcessDialogSetItemType( value : string ) {
        const {
                  createNewBusinessProcessDialogItemType,
                  createNewBusinessProcessDialogSchema
              }               = this.state,
              oldGeneratedKey = createNewBusinessProcessDialogSchema === 'CFG'
                  ? `CFG:${createNewBusinessProcessDialogItemType ?? ''}`
                  : createNewBusinessProcessDialogItemType ?? '';

        let {
                createNewBusinessProcessDialogKey,
                createNewBusinessProcessDialogKeyError
            } = this.state;

        if ( !createNewBusinessProcessDialogKey || createNewBusinessProcessDialogKey === oldGeneratedKey ) {
            createNewBusinessProcessDialogKey = createNewBusinessProcessDialogSchema === 'CFG' ? 'CFG:' + value : value;
            createNewBusinessProcessDialogKeyError = undefined;
        }

        this.setState( {
            createNewBusinessProcessDialogItemType: value,
            createNewBusinessProcessDialogKey,
            createNewBusinessProcessDialogKeyError
        } );
    }

    onSelectStepOrConnection( selectedStepId? : number, selectedConnectionId? : number ) {
        this.props.setParam?.( this.props.uid, 'selectedStepId', selectedStepId );
        this.props.setParam?.( this.props.uid, 'selectedConnectionId', selectedConnectionId );
        this.props.setParam?.( this.props.uid, 'selectedDataObjectId', undefined );
    }

    onSelectDataObject( selectedDataObjectId? : number ) {
        this.props.setParam?.( this.props.uid, 'selectedDataObjectId', selectedDataObjectId );
        this.props.setParam?.( this.props.uid, 'selectedStepId', undefined );
        this.props.setParam?.( this.props.uid, 'selectedConnectionId', undefined );
    }

    onMatchMobileChangeHandler() {
        this.setState( { mobile: getMobile() } );
    }

    getSelectableSchemata( user? : User ) {
        const retVal : Schema[] = [];

        for ( const schema of schemata ) {
            const itemTypes = metaModel[ schema ].getItemTypes() ?? [];

            for ( const itemType of itemTypes ) {
                if (
                    itemType.id > 0
                    && user?.hasGenericPermissionOnItemType( 'read', itemType.id, schema )
                ) {
                    retVal.push( schema );
                    break;
                }
            }
        }

        retVal.sort();

        return retVal;
    }

    getCreateNewBusinessProcessDialog() {
        const {
                  uid,
                  user
              }                           = this.props,
              {
                  showCreateNewBusinessProcessDialog,
                  createNewBusinessProcessDialogKey,
                  createNewBusinessProcessDialogKeyError,
                  createNewBusinessProcessDialogSchema,
                  createNewBusinessProcessDialogItemType
              }                           = this.state,
              okDisabled                  = Boolean( !createNewBusinessProcessDialogKey || !createNewBusinessProcessDialogItemType ),
              selectableSchemata          = this.getSelectableSchemata( user ),
              style : React.CSSProperties = {
                  width            : '33rem',
                  minWidth         : '33rem',
                  [ '--min-width' ]: '33rem',
                  minHeight        : '25vh'
              };

        // only one selectable schemata and not set -> set it
        if ( selectableSchemata.length === 1 && createNewBusinessProcessDialogSchema !== selectableSchemata[ 0 ] ) {
            this.setState( { createNewBusinessProcessDialogSchema: selectableSchemata[ 0 ] } );
            return;
        }

        return <Dialog
            key={`${uid}_createNewBusinessProcessDialog`}
            id={`${uid}_createNewBusinessProcessDialog`}

            visible={showCreateNewBusinessProcessDialog}
            onHide={this.onCreateNewBusinessProcessDialogHide}
            focusOnShow={false}
            closeOnEscape={false}
            className="yn-businessProcess-dialog yn-businessProcess-create-new-dialog"
            style={style}
            appendTo={( document.querySelector( '.yn-tabpanel .p-tabview-panel' ) ?? undefined ) as HTMLElement | undefined}
            header={__( 'YN.base.frontend.businessProcessDesigner.createNewDialog.title' )}
            footer={<div>
                <Button
                    id={`${uid}_okButton`}
                    key="okButton"
                    label={__( 'YN.base.common.ok' )}
                    className="p-button-outlined mr-3"
                    disabled={okDisabled}
                    onClick={this.onCreateNewBusinessProcessDialogOkButtonClick}
                />
                <Button
                    id={`${uid}_cancelButton`}
                    key="cancelButton"
                    label={__( 'YN.base.common.cancel' )}
                    className="p-button-secondary p-button-outlined"
                    onClick={this.onCreateNewBusinessProcessDialogHide}
                />
            </div>}
        >
            <StringField
                uid={`${uid}_keyField`}
                label={__( 'YN.base.frontend.businessProcessDesigner.createNewDialog.key.label' )}
                value={createNewBusinessProcessDialogKey}
                errorMessage={createNewBusinessProcessDialogKeyError}
                style={{ width: '30rem' }}
                labelPosition="top"
                infoTextLayout="hidden"
                markDirty={false}
                onChange={this.onCreateNewBusinessProcessDialogSetKey}
            />
            <FieldWrapper
                uid={`${uid}_itemTypeField`}
                label={__( 'YN.base.frontend.businessProcessDesigner.createNewDialog.itemType.label' )}
                labelPosition="top"
                infoTextLayout="hidden"
                isDirty={false}
                autoComplete={false}
            >
                {selectableSchemata.length > 1 &&
                    <SchemaDropdown
                        uid={uid}
                        value={createNewBusinessProcessDialogSchema}
                        onChange={this.onCreateNewBusinessProcessDialogSetSchema}

                        style={{ flex: '0 0 7rem', width: '7rem', paddingRight: '.5rem' }}
                    />
                }
                <ItemTypeDropdown
                    uid={uid}

                    schema={createNewBusinessProcessDialogSchema}
                    value={createNewBusinessProcessDialogItemType}
                    rawMode={false}
                    showAdditionalRawNames={false}
                    markDirty={false}
                    onChange={this.onCreateNewBusinessProcessDialogSetItemType}
                    inputStyle={{ width: selectableSchemata.length > 1 ? '23rem' : '30rem' }}
                />
            </FieldWrapper>
        </Dialog>;
    }

    getHeaderbar( uid : string, businessProcessKey : string, businessProcessName : string ) {
        return <Headerbar
            key={`${uid}_headerbar`}

            uid={uid}

            businessProcessKey={businessProcessKey}
            businessProcessName={businessProcessName}
        />;
    }

    getToolbar( uid : string, dataContext : string, rootItemId : number, rootItem : Item, saveButtonErrorCount : number, saveButtonErrorTooltip : React.ReactNode | React.ReactNode[] ) {
        return <Toolbar
            key={`${uid}_ItemFinderToolbar`}

            uid={uid}

            dataContext={dataContext}
            schema="CFG"

            rootItemId={rootItemId}

            treeViewItem={rootItem}

            saveButtonErrorCount={saveButtonErrorCount}
            saveButtonErrorTooltip={saveButtonErrorTooltip}

            onAddBusinessProcessStepButtonClick={this.onAddNewBusinessProcessStep}
            onReloadButtonClick={this.onReloadButtonClick}
        />;
    }

    getDesignerTree( uid : string, dataContext : string, treeProps : any, parentUid : string, rootItemId : number, additionalElusiveDataContexts : string[], selectedDataObjectId : number | undefined ) {
        return <DesignerTree
            key={uid}

            ref={( el ) => this.tree = el}

            {...treeProps}

            uid={uid}
            dataContext={dataContext}
            parentUid={parentUid}
            rootItemId={rootItemId}

            selectedLayoutElementId={selectedDataObjectId}
            additionalElusiveDataContexts={additionalElusiveDataContexts}

            onSelectLayoutElement={this.onSelectDataObject}
            //onMarkLayoutElementsForAlias={this.onMarkLayoutElementsForAlias}

            className="yn-sidebar-content p-tree"
        />;
    }

    getDesignerTreeEqual( newArgs : any[], lastArgs : any[] ) : boolean {
        return objectUtils.getDiffKeys( { ...newArgs }, { ...lastArgs } ).length === 0;
    }

    getBusinessProcessDesigner( configUid : string, configDataContext : string, uid : string, businessProcessDesignerProps : any, configRootItemId : number, additionalElusiveDataContexts : string[], selectedStepId : number | undefined, selectedConnectionId : number | undefined ) {
        return <BusinessProcessDesigner
            key={uid}

            ref={( el ) => this.businessProcessDesigner = el}

            {...businessProcessDesignerProps}

            uid={uid}
            configUid={configUid}

            configDataContext={configDataContext}
            configRootItemId={configRootItemId}

            additionalElusiveDataContexts={additionalElusiveDataContexts}

            selectedStepId={selectedStepId}
            selectedConnectionId={selectedConnectionId}
            onSelectStepOrConnection={this.onSelectStepOrConnection}
        />;
    }

    getBusinessProcessDesignerEqual( newArgs : any[], lastArgs : any[] ) : boolean {
        return objectUtils.getDiffKeys( { ...newArgs }, { ...lastArgs } ).length === 0;
    }

    getForm( uid : string, dataContext : string, formProps : any, additionalElusiveDataContexts : string[], viewKey : string, rootItemId : number, treeParentItemId : number, treeRootItemId : number, treeVariables : Variables | undefined ) {
        return <Form
            key={uid}

            {...formProps}

            uid={uid}
            dataContext={dataContext}
            additionalElusiveDataContexts={additionalElusiveDataContexts}
            schema="CFG"
            viewKey={viewKey}
            mode="embedded"
            rootItemId={rootItemId}
            rootItemKey=""
            rootItemType=""
            treeParentItemId={treeParentItemId}
            treeRootItemId={treeRootItemId}
            treeVariables={treeVariables}
        />;
    }

    getFormEqual( newArgs : any[], lastArgs : any[] ) : boolean {
        return objectUtils.getDiffKeys( { ...newArgs }, { ...lastArgs } ).length === 0;
    }

    /**
     * React render method
     * @return {*}
     */
    render() {
        const {
                  dataContext,
                  elusiveData,
                  menu2Width,
                  menuWidth,
                  selectedConnectionId,
                  selectedDataObjectId,
                  selectedStepId,
                  status,
                  subPanelProps,
                  timestamp,
                  timezone,
                  uid
              }                              = this.props as AllProps,
              {
                  errorCount,
                  errorMessage,
                  errorTooltip,
                  initialized,
                  rootItemId = 0,
                  showCreateNewBusinessProcessDialog
              }                              = this.state,
              rootItem                       = global.data[ dataContext ]?.items?.[ rootItemId ],
              rawMode                        = false,
              showAdditionalRawNames         = false,
              selectedLayoutElement          = selectedStepId
                  ? global.data[ dataContext ]?.items[ selectedStepId ]
                  : selectedDataObjectId
                      ? global.data[ dataContext ]?.items[ selectedDataObjectId ]
                      : undefined,
              currentItemId                  = selectedLayoutElement?.id ?? rootItemId,
              currentItem                    = selectedLayoutElement ?? rootItem,
              panelConfigs                   = user && currentItem ? user.getPanelPropsForItemType( 'CFG', currentItem.itemTypeId, undefined, 'Form' ) as FormPanelProps[] : undefined,
              panelConfig                    = ( selectedConnectionId
                      ? panelConfigs?.find( panelProps => panelProps.viewKey?.match( /^BPdSp_Reference_/ ) )
                      : undefined )
                  ?? panelConfigs?.find( panelProps => panelProps.viewKey?.match( /^BPdSp_/ ) )
                  ?? panelConfigs?.find( panelProps => panelProps.viewKey?.match( /^XdSp_/ ) ) ?? panelConfigs?.[ 0 ] ?? null,
              allBelongsToReferenceSetIds    = currentItem ? metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( currentItem.itemTypeId, '*', 'belongsTo', 'src' )?.map( rset => rset.id ) : undefined,
              parentPayloadId                = dataUtils.findReferences( dataContext, allBelongsToReferenceSetIds, undefined, currentItemId, true )?.[ 0 ]?.destItemId ?? rootItemId,
              designerTreeUid                = `tree-${rootItemId}`,
              fullDesignerTreeUid            = `${uid}_${designerTreeUid}`,
              formUid                        = `form-${currentItemId}${selectedConnectionId ? `-${selectedConnectionId}` : ''}`,
              fullFormUid                    = `${uid}_${formUid}`,
              businessProcessDesignerUid     = `businessProcessDesigner-${rootItemId}`,
              fullBusinessProcessDesignerUid = `${uid}_${businessProcessDesignerUid}`,
              attributesUid                  = `attributes-${currentItemId}`,
              fullAttributesUid              = `${uid}_${attributesUid}`,
              //
              headerbar                      = this.getHeaderbar(
                  uid,
                  rootItem?.key ?? '',
                  rootItem?.name ?? ''
              ),
              toolbar                        = this.getToolbar(
                  uid,
                  dataContext,
                  rootItemId,
                  rootItem,
                  errorCount,
                  errorTooltip
              ),
              menu                           = initialized
                  ? this.getDesignerTree(
                      fullDesignerTreeUid,
                      dataContext,
                      subPanelProps?.[ designerTreeUid ],
                      uid,
                      rootItemId,
                      [ fullBusinessProcessDesignerUid, fullFormUid, uid ],
                      selectedDataObjectId
                  )
                  : <></>,
              designer                       = initialized
                  ? this.getBusinessProcessDesigner(
                      uid,
                      dataContext,
                      fullBusinessProcessDesignerUid,
                      subPanelProps?.[ businessProcessDesignerUid ],
                      rootItemId,
                      [ fullDesignerTreeUid, fullFormUid, uid ],
                      selectedStepId,
                      selectedConnectionId
                  )
                  : undefined,
              form                           = initialized && panelConfig?.panelType === 'Form' && currentItemId
                  ? this.getForm(
                      fullFormUid,
                      dataContext,
                      subPanelProps?.[ formUid ],
                      [ fullDesignerTreeUid, fullBusinessProcessDesignerUid, uid ],
                      panelConfig.viewKey,
                      currentItemId,
                      parentPayloadId,
                      rootItemId,
                      {
                          selectedConnectionId
                      }
                  )
                  : undefined,
              genericForm                    = initialized && panelConfig?.panelType !== 'Form' && currentItem
                  ? <div key={`${uid}_formview`}>
                      <AttributesForm
                          key={fullAttributesUid}

                          {...subPanelProps?.[ attributesUid ]}

                          uid={fullAttributesUid}
                          dataContext={dataContext}
                          style={{ padding: 0 }}
                          schema="CFG"
                          status={status}
                          timestamp={timestamp}
                          timezone={timezone}
                          item={currentItem}
                          rawMode={rawMode}
                          showAdditionalRawNames={showAdditionalRawNames}
                          editable
                      />
                  </div>
                  : undefined;

        return <>
            {showCreateNewBusinessProcessDialog &&
                this.getCreateNewBusinessProcessDialog()
            }
            {elusiveData?.showCouldNotSavePopup &&
                <Dialog
                    key={`${uid}_dialog`}
                    header={__( 'YN.base.frontend.dataTable.couldNotSaveWhileErrors.title' )}
                    footer={<div><Button label="OK" className="p-button-outlined"
                                         onClick={() => this.props.setElusiveData( uid, { showCouldNotSavePopup: false } )}/></div>}
                    maskClassName="yn-dialog-panel-modal"
                    appendTo="self"
                    position="top"
                    visible={true}
                    style={{ width: 'auto', maxWidth: '80%' }}
                    closable={false}
                    className="yn-error-dialog"
                    onHide={() => {
                    }}>
                    {errorMessage}
                </Dialog>
            }
            {headerbar}
            {toolbar}
            <LoadingMaskPanel forComponent={uid} className="yn-flex-layout-parent yn-businessprocess-wrapper">
                <Toast
                    ref={( el ) => this.toast = el}

                    key={`${uid}_toast`}

                    position="bottom-right"
                    style={{ position: 'absolute', marginTop: '0' }}
                />
                <ResizeableLeftMenuPanel
                    key={`${uid}_menuContentPanel`}

                    uid={uid}

                    menuWidth={menuWidth}
                    minMenuWidth={15}
                    maxMenuWidth={25}
                    contentStyle={this.menuContentPanelContentStyle}
                    onMenuScroll={this.onMenuScroll}
                    onMenuWidthChange={this.onMenuWidthChange}

                    menu={menu}
                    content={<div className="yn-flex-layout-parent" style={{ width: '100%' }}>
                        <ResizeableRightMenuPanel
                            key={`${uid}_menu2ContentPanel`}

                            uid={uid}

                            menuWidth={menu2Width}
                            minMenuWidth={15}
                            maxMenuWidth={33}
                            menuClassName="yn-content-panel-container yn-menu-form yn-formdesigner-properties-panel"
                            onMenuWidthChange={this.onMenu2WidthChange}

                            content={<LoadingMaskPanel
                                forComponent={fullBusinessProcessDesignerUid}
                                className="yn-flex-layout-parent yn-content-panel-container"
                                style={{ position: 'absolute', width: '100% !important', height: '100% !important' }}
                            >
                                {designer}
                            </LoadingMaskPanel>}
                            menu={initialized
                                ? <LoadingMaskPanel
                                    key={`${uid}_businessProcessview_loadingmask`}
                                    forComponent={panelConfig?.panelType === 'Form' ? fullFormUid : fullAttributesUid}
                                    className="yn-flex-layout-parent"
                                >
                                    <div>
                                        {form}
                                        {genericForm}
                                    </div>
                                </LoadingMaskPanel>
                                : <></>
                            }
                        />
                    </div>}
                />
            </LoadingMaskPanel>
        </>;
    }
}


// redux stuff

/**
 *
 * @param state
 * @param ownProps
 */
function mapStateToProps( state : StoreState, ownProps : AllProps ) {
    const retVal : Partial<Props> = {
        allElusiveData  : undefined,
        elusiveData     : undefined,
        intercomMessages: undefined,
        user            : undefined
    };

    retVal.allElusiveData = state.elusiveData;

    if ( ownProps.uid ) {

        // get elusiveData
        if ( state.elusiveData?.[ ownProps.uid ] ) {
            retVal.elusiveData = state.elusiveData[ ownProps.uid ];
        }

        // get intercom messages
        if ( state.intercom?.[ ownProps.uid ] ) {
            retVal.intercomMessages = state.intercom[ ownProps.uid ];
        }

        // get the running xhr request belonging to this component (uid)
        if ( state.xhr?.[ ownProps.uid ] ) {
            retVal.xhrRequestsForComponent = state.xhr[ ownProps.uid ];
        }
    }

    // get some session user inbusinessProcessation
    if ( state.session?.user ) {
        retVal.user = state.session.user;
    }

    return retVal;
}

/**
 * Bind action creators to component props
 * @param dispatch
 * @return {*}
 */
function mapDispatchToProps( dispatch : Dispatch ) {
    return bindActionCreators( Actions, dispatch );
}

export default connect( mapStateToProps, mapDispatchToProps )( BusinessProcessDesignerView );
